import React from "react";
import { Link } from "react-router-dom";
import { useStaffAuth } from "../../context/StaffAuthContext";

// Safe UI imports with fallbacks
import * as UI from "../../components/ui";

const Card = UI.Card || (({ className, children }) => (
  <div className={`bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800/80 rounded-2xl shadow-xs ${className}`}>
    {children}
  </div>
));

const Badge = UI.Badge || (({ children, className }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-extrabold ${className}`}>
    {children}
  </span>
));

const Alert = UI.Alert || (({ children, variant, className }) => (
  <div className={`p-4 rounded-xl text-xs font-bold ${variant === "danger" ? "bg-rose-50 text-rose-700 border border-rose-200" : "bg-neutral-50 dark:bg-neutral-950 text-neutral-700 dark:text-neutral-200 border border-neutral-200 dark:border-neutral-800"} ${className}`}>
    {children}
  </div>
));

/**
 * Analytics hub — landing page with links to dedicated analytics pages.
 * Mounted at two routes in App.js:
 *   owner: /analytics              -> <AnalyticsHub isPortal={false} />
 *   staff: /staff-panel/analytics  -> <AnalyticsHub isPortal={true} />
 *
 * On portal, links are filtered by hasPermission() using existing module keys.
 * Owner sees all links (no permission gating on owner routes).
 */
const ANALYTICS_LINKS = [
  {
    title: "Student Analytics",
    description: "Student enrolment, class/section breakdown and student attendance trends.",
    path: "students",
    permissions: [
      "STUDENT_MANAGEMENT_VIEW",
      "STUDENT_MANAGEMENT_FULL",
      "STUDENT_ATTENDANCE_VIEW",
    ],
    accent: "hover:border-indigo-400 hover:bg-indigo-50/30",
    badge: "bg-indigo-50 text-indigo-700 group-hover:bg-indigo-100",
  },
  {
    title: "Staff Analytics",
    description: "Staff roster, roles breakdown and staff attendance trends.",
    path: "staff",
    permissions: [
      "STAFF_MANAGEMENT_VIEW",
      "STAFF_MANAGEMENT_FULL",
      "STAFF_ATTENDANCE_VIEW",
    ],
    accent: "hover:border-sky-400 hover:bg-sky-50/30",
    badge: "bg-sky-50 text-sky-700 group-hover:bg-sky-100",
  },
  {
    title: "Fee Analytics",
    description: "Collections, dues, payment modes and fee status breakdowns.",
    path: "fees",
    permissions: ["FEE_MANAGEMENT_VIEW", "FEE_MANAGEMENT_FULL"],
    accent: "hover:border-teal-400 hover:bg-teal-50/30",
    badge: "bg-teal-50 text-teal-700 group-hover:bg-teal-100",
  },
  {
    title: "Payroll Analytics",
    description: "Payroll runs, payouts, salary components and staff cost trends.",
    path: "payroll",
    permissions: ["PAYROLL_MANAGEMENT_VIEW", "PAYROLL_MANAGEMENT_FULL"],
    accent: "hover:border-emerald-400 hover:bg-emerald-50/30",
    badge: "bg-emerald-50 text-emerald-700 group-hover:bg-emerald-100",
  },
  {
    title: "Exams & Marks",
    description: "Exam schedules, marks distribution and performance summaries.",
    path: "exams",
    permissions: ["EXAM_MANAGEMENT_VIEW"],
    accent: "hover:border-violet-400 hover:bg-violet-50/30",
    badge: "bg-violet-50 text-violet-700 group-hover:bg-violet-100",
  },
];

const AnalyticsHub = ({ isPortal = false }) => {
  const { hasPermission } = useStaffAuth();
  const homeUrl = isPortal ? "/staff-home" : "/school-home";
  const basePath = isPortal ? "/staff-panel/analytics" : "/analytics";

  const visibleLinks = isPortal
    ? ANALYTICS_LINKS.filter((link) =>
        link.permissions.some((key) => hasPermission(key))
      )
    : ANALYTICS_LINKS;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      {/* Top Breadcrumb & Header */}
      <div className="pb-4 border-b border-neutral-200 dark:border-neutral-800/80">
        <nav className="mb-2 flex items-center gap-2 text-xs font-extrabold text-neutral-400 dark:text-neutral-500">
          <Link to={homeUrl} className="hover:text-indigo-600 transition-colors">
            Home
          </Link>
          <span>/</span>
          <span className="text-neutral-800 dark:text-neutral-100">Analytics</span>
        </nav>

        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
          Analytics Hub
        </h1>
        <p className="text-xs sm:text-sm font-semibold text-neutral-500 dark:text-neutral-400 mt-1">
          Open a dedicated analytics workspace for each operational module
        </p>
      </div>

      {/* Main Grid View */}
      {visibleLinks.length === 0 ? (
        <Alert variant="secondary" className="py-8 text-center text-neutral-500 dark:text-neutral-400">
          No analytics modules available for your current role and permissions.
        </Alert>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {visibleLinks.map((link) => (
            <Link
              key={link.path}
              to={`${basePath}/${link.path}`}
              className="group block"
            >
              <Card
                className={`h-full p-5 sm:p-6 flex flex-col justify-between transition-all duration-150 ${link.accent}`}
              >
                <div>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <h2 className="text-base font-extrabold text-neutral-900 dark:text-neutral-100 group-hover:text-indigo-600 transition-colors">
                      {link.title}
                    </h2>
                  </div>
                  <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 leading-relaxed mb-6">
                    {link.description}
                  </p>
                </div>

                <div>
                  <Badge className={`transition-colors ${link.badge}`}>
                    View Analytics &rarr;
                  </Badge>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnalyticsHub;